import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PERMISSIONS, ROLES } from '@lms/shared-types';
import { ApiRequestError, api } from '@/lib/api';
import { rememberServerNow } from '@/lib/attempt-clock';
import { attemptConnection } from '@/lib/attempt-connection';
import { withCsrfHeaders } from '@/lib/api-headers';
import { registerLearningSessionEndHandler } from '@/lib/learning-session-end';
import { usePermissions } from '@/hooks/usePermissions';
import { selectIsAuthenticated, useAuthStore } from '@/stores/authStore';

const UI_TICK_MS = 1_000;
const DEFAULT_TICK_SECONDS = 15;
const DEFAULT_PULSE_SECONDS = 30;
const LEADER_CHANNEL = 'lms-learning-session';
const CLIENT_SESSION_KEY = 'lms.clientSessionId';

function maxPulseDeltaSeconds(pulseIntervalSeconds: number): number {
  return Math.max(90, pulseIntervalSeconds * 3);
}

export interface SessionConfig {
  maxAfkMinutes: number;
  maxSessionSeconds: number;
  maxDailySeconds: number;
  tickIntervalSeconds: number;
  pulseIntervalSeconds: number;
  timezone: string;
  secondsUsedToday: number;
  secondsUsedInSession: number;
  secondsRemainingSession: number;
  secondsRemainingToday: number;
  sessionClosed?: boolean;
}

interface PulseResponse {
  acceptedSeconds: number;
  sessionClosed?: boolean;
  sessionLimitReached?: boolean;
  dailyLimitReached?: boolean;
  secondsRemainingSession?: number;
  secondsRemainingToday?: number;
}

function getOrCreateClientSessionId(): string {
  const existing = sessionStorage.getItem(CLIENT_SESSION_KEY);
  if (existing) return existing;
  const id = crypto.randomUUID();
  sessionStorage.setItem(CLIENT_SESSION_KEY, id);
  return id;
}

function parsePlayContext(pathname: string): { courseId?: string; sectionItemId?: string } {
  const studentMatch = /^\/mis-cursos\/([^/]+)\/play\/([^/]+)/.exec(pathname);
  if (studentMatch) {
    return { courseId: studentMatch[1], sectionItemId: studentMatch[2] };
  }
  return {};
}

async function sendPulse(body: Record<string, unknown>, beacon = false): Promise<PulseResponse | null> {
  try {
    if (beacon) {
      // sendBeacon no permite cabeceras personalizadas; la API exige X-Requested-With (CSRF).
      void fetch('/api/student/session/pulse/beacon', {
        method: 'POST',
        credentials: 'include',
        headers: withCsrfHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body),
        keepalive: true,
      }).catch(() => {});
      return null;
    }
    const started = attemptConnection.begin();
    try {
      const result = await api<PulseResponse & { serverNow?: string }>('/student/session/pulse', { method: 'POST', body });
      attemptConnection.report(200, started);
      if (result.serverNow) rememberServerNow(result.serverNow);
      return result;
    } catch (error) {
      attemptConnection.report(error instanceof ApiRequestError ? error.status : 0, started);
      return null;
    }
  } catch {
    return null;
  }
}

export function useLearningSessionTracker(options: { enabled: boolean }) {
  const location = useLocation();
  const pathnameRef = useRef(location.pathname);
  pathnameRef.current = location.pathname;
  const userId = useAuthStore((s) => s.user?.id);
  const clientSessionIdRef = useRef(getOrCreateClientSessionId());
  const [config, setConfig] = useState<SessionConfig | null>(null);
  const [liveStatus, setLiveStatus] = useState({ currentSessionSeconds: 0, todaySeconds: 0 });
  const configRef = useRef<SessionConfig | null>(null);
  const bufferRef = useRef(0);
  const lastActivityRef = useRef(Date.now());
  const lastPulseRef = useRef(Date.now());
  const lastTickRef = useRef(Date.now());
  const isLeaderRef = useRef(true);
  const isVisibleRef = useRef(document.visibilityState === 'visible');
  const stoppedRef = useRef(false);
  const endingSessionRef = useRef(false);
  const pausedUntilActivityRef = useRef(false);
  const mediaPlayingRef = useRef(false);
  const activityEngagedRef = useRef(false);
  const tickMsRef = useRef(DEFAULT_TICK_SECONDS * 1000);
  const pulseMsRef = useRef(DEFAULT_PULSE_SECONDS * 1000);
  const maxPulseDeltaRef = useRef(maxPulseDeltaSeconds(DEFAULT_PULSE_SECONDS));

  const applyTimingFromConfig = useCallback((settings: SessionConfig) => {
    const tickSeconds = settings.tickIntervalSeconds || DEFAULT_TICK_SECONDS;
    const pulseSeconds = settings.pulseIntervalSeconds || DEFAULT_PULSE_SECONDS;
    tickMsRef.current = tickSeconds * 1000;
    pulseMsRef.current = pulseSeconds * 1000;
    maxPulseDeltaRef.current = maxPulseDeltaSeconds(pulseSeconds);
  }, []);

  const isActive = useCallback(() => {
    if (pausedUntilActivityRef.current) return false;
    if (!options.enabled || stoppedRef.current || !isLeaderRef.current || !isVisibleRef.current) {
      return false;
    }
    const settings = configRef.current;
    if (!settings) return false;
    if (settings.secondsRemainingToday <= 0 || settings.secondsRemainingSession <= 0) {
      return false;
    }
    if (mediaPlayingRef.current || activityEngagedRef.current) return true;
    const idleMs = settings.maxAfkMinutes * 60_000;
    return Date.now() - lastActivityRef.current < idleMs;
  }, [options.enabled]);

  const isAfkExceeded = useCallback(() => {
    if (pausedUntilActivityRef.current) return false;
    if (!options.enabled || stoppedRef.current || endingSessionRef.current) return false;
    if (!isLeaderRef.current) return false;
    if (mediaPlayingRef.current || activityEngagedRef.current) return false;
    const settings = configRef.current;
    if (!settings) return false;
    const idleMs = settings.maxAfkMinutes * 60_000;
    return Date.now() - lastActivityRef.current >= idleMs;
  }, [options.enabled]);

  const rotateClientSessionId = useCallback(() => {
    const newId = crypto.randomUUID();
    clientSessionIdRef.current = newId;
    sessionStorage.setItem(CLIENT_SESSION_KEY, newId);
    return newId;
  }, []);

  const syncActiveBuffer = useCallback((force = false) => {
    if (pausedUntilActivityRef.current) return;
    if (!options.enabled || stoppedRef.current || !isLeaderRef.current) {
      return;
    }
    if (!force && !isVisibleRef.current) {
      return;
    }
    const settings = configRef.current;
    if (!settings) return;
    if (settings.secondsRemainingToday <= 0 || settings.secondsRemainingSession <= 0) {
      return;
    }
    const idleMs = settings.maxAfkMinutes * 60_000;
    const isCounting =
      mediaPlayingRef.current ||
      activityEngagedRef.current ||
      Date.now() - lastActivityRef.current < idleMs;
    if (!isCounting) {
      return;
    }
    const elapsedSeconds = Math.floor((Date.now() - lastTickRef.current) / 1000);
    if (elapsedSeconds > 0) {
      bufferRef.current += elapsedSeconds;
      lastTickRef.current += elapsedSeconds * 1000;
    }
  }, [options.enabled]);

  const publishLiveStatus = useCallback(() => {
    syncActiveBuffer();
    const settings = configRef.current;
    if (!settings) {
      setLiveStatus({ currentSessionSeconds: 0, todaySeconds: 0 });
      return;
    }
    const pending = bufferRef.current;
    setLiveStatus({
      currentSessionSeconds: settings.secondsUsedInSession + pending,
      todaySeconds: settings.secondsUsedToday + pending,
    });
  }, [syncActiveBuffer]);

  const ensureSessionStarted = useCallback(async () => {
    if (pausedUntilActivityRef.current) return;
    if (!options.enabled || stoppedRef.current || endingSessionRef.current) return;
    await sendPulse({
      clientSessionId: clientSessionIdRef.current,
      deltaSeconds: 0,
      context: parsePlayContext(pathnameRef.current),
    });
  }, [options.enabled]);

  const resumeAfterAfk = useCallback(() => {
    if (!pausedUntilActivityRef.current) return;
    pausedUntilActivityRef.current = false;
    lastActivityRef.current = Date.now();
    void ensureSessionStarted();
  }, [ensureSessionStarted]);

  const markActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    resumeAfterAfk();
  }, [resumeAfterAfk]);

  const setMediaPlaying = useCallback(
    (playing: boolean) => {
      mediaPlayingRef.current = playing;
      if (playing) {
        lastActivityRef.current = Date.now();
        resumeAfterAfk();
      }
    },
    [resumeAfterAfk],
  );

  const setActivityEngaged = useCallback(
    (engaged: boolean) => {
      activityEngagedRef.current = engaged;
      if (engaged) {
        lastActivityRef.current = Date.now();
        resumeAfterAfk();
      }
    },
    [resumeAfterAfk],
  );

  const resetTrackerState = useCallback(() => {
    stoppedRef.current = false;
    endingSessionRef.current = false;
    pausedUntilActivityRef.current = false;
    bufferRef.current = 0;
    lastActivityRef.current = Date.now();
    lastPulseRef.current = Date.now();
    lastTickRef.current = Date.now();
    isVisibleRef.current = document.visibilityState === 'visible';
  }, []);

  const flush = useCallback(
    async (
      beacon = false,
      endSession = false,
      endReason: 'logout' | 'unload' | 'afk' = 'unload',
      retriedAfterClose = false,
    ) => {
      if (!options.enabled) return;

      syncActiveBuffer(true);

      const deltaSeconds = Math.min(bufferRef.current, maxPulseDeltaRef.current);
      const shouldEndSession = endSession && bufferRef.current <= maxPulseDeltaRef.current;

      if (!endSession && deltaSeconds <= 0) return;
      if (endSession && endReason === 'afk' && pausedUntilActivityRef.current) return;
      if (endSession) {
        endingSessionRef.current = true;
      }

      const response = await sendPulse(
        {
          clientSessionId: clientSessionIdRef.current,
          deltaSeconds,
          context: parsePlayContext(pathnameRef.current),
          endSession: shouldEndSession,
          endReason: shouldEndSession ? endReason : undefined,
          ...(shouldEndSession && endReason === 'afk'
            ? { afkIdleSeconds: Math.round((Date.now() - lastActivityRef.current) / 1000) }
            : {}),
        },
        beacon,
      );

      if (response?.sessionClosed && !endSession) {
        rotateClientSessionId();
        pausedUntilActivityRef.current = false;
        endingSessionRef.current = false;
        if (!retriedAfterClose) {
          await flush(false, false, 'unload', true);
        }
        return;
      }

      const pulseAccepted = beacon || response != null;
      if (pulseAccepted) {
        bufferRef.current = Math.max(0, bufferRef.current - deltaSeconds);
        lastPulseRef.current = Date.now();
      } else if (!endSession) {
        return;
      }

      if (shouldEndSession && pulseAccepted) {
        if (endReason === 'afk') {
          pausedUntilActivityRef.current = true;
          rotateClientSessionId();
          bufferRef.current = 0;
          lastTickRef.current = Date.now();
          if (!beacon) {
            try {
              const data = await api<SessionConfig>(
                `/student/session/config?clientSessionId=${encodeURIComponent(clientSessionIdRef.current)}`,
              );
              configRef.current = data;
              setConfig(data);
            } catch {
              // ignore
            }
          }
          endingSessionRef.current = false;
        } else {
          stoppedRef.current = true;
          sessionStorage.removeItem(CLIENT_SESSION_KEY);
        }
      }

      if (response) {
        if (response.sessionLimitReached || response.dailyLimitReached) {
          stoppedRef.current = true;
        }
        setConfig((prev) => {
          if (!prev) return prev;
          const next = {
            ...prev,
            secondsRemainingSession: response.secondsRemainingSession ?? prev.secondsRemainingSession,
            secondsRemainingToday: response.secondsRemainingToday ?? prev.secondsRemainingToday,
            secondsUsedInSession:
              prev.maxSessionSeconds - (response.secondsRemainingSession ?? prev.secondsRemainingSession),
            secondsUsedToday:
              prev.maxDailySeconds - (response.secondsRemainingToday ?? prev.secondsRemainingToday),
          };
          configRef.current = next;
          return next;
        });
      }

      if (endSession && bufferRef.current > 0 && pulseAccepted) {
        await flush(beacon, true, endReason);
        return;
      }

      if (endSession && !pulseAccepted) {
        endingSessionRef.current = false;
      }
    },
    [options.enabled, rotateClientSessionId, syncActiveBuffer],
  );

  const endSessionNow = useCallback(
    async (reason: 'logout' | 'unload') => {
      syncActiveBuffer(true);
      await flush(false, true, reason);
    },
    [flush, syncActiveBuffer],
  );

  const endSessionNowRef = useRef(endSessionNow);
  endSessionNowRef.current = endSessionNow;

  useEffect(() => {
    if (!options.enabled) return;
    return registerLearningSessionEndHandler(async (reason) => {
      await endSessionNowRef.current(reason);
    });
  }, [options.enabled]);

  const enabledPrevRef = useRef(options.enabled);
  useEffect(() => {
    if (options.enabled && !enabledPrevRef.current) {
      resetTrackerState();
    }
    enabledPrevRef.current = options.enabled;
  }, [options.enabled, resetTrackerState]);

  const lastPathRef = useRef(location.pathname);
  useEffect(() => {
    if (!options.enabled) return;
    if (lastPathRef.current === location.pathname) return;
    lastPathRef.current = location.pathname;
    syncActiveBuffer(true);
    void flush(false);
  }, [location.pathname, options.enabled, syncActiveBuffer, flush]);

  useEffect(() => {
    if (!options.enabled) return;

    let cancelled = false;

    const applyConfig = async (data: SessionConfig) => {
      applyTimingFromConfig(data);
      configRef.current = data;
      setConfig(data);
      markActivity();
      publishLiveStatus();
      if (data.secondsRemainingToday <= 0 || data.secondsRemainingSession <= 0) {
        stoppedRef.current = true;
        return;
      }
      await ensureSessionStarted();
    };

    api<SessionConfig>(
      `/student/session/config?clientSessionId=${encodeURIComponent(clientSessionIdRef.current)}`,
    )
      .then(async (data) => {
        if (cancelled) return;
        if (data.sessionClosed) {
          rotateClientSessionId();
          const refreshed = await api<SessionConfig>(
            `/student/session/config?clientSessionId=${encodeURIComponent(clientSessionIdRef.current)}`,
          );
          if (cancelled) return;
          await applyConfig(refreshed);
          return;
        }
        await applyConfig(data);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [
    options.enabled,
    userId,
    rotateClientSessionId,
    markActivity,
    publishLiveStatus,
    ensureSessionStarted,
    applyTimingFromConfig,
  ]);

  useEffect(() => {
    if (!options.enabled || !config) return;

    const suspendGapMs = tickMsRef.current * 3;
    const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(LEADER_CHANNEL) : null;
    const tabId = crypto.randomUUID();

    function claimLeadership() {
      if (!channel) {
        isLeaderRef.current = true;
        return;
      }
      channel.postMessage({ type: 'leader-claim', tabId, visible: isVisibleRef.current });
    }

    channel?.addEventListener('message', (event: MessageEvent<{ type: string; tabId: string; visible?: boolean }>) => {
      if (event.data?.type !== 'leader-claim') return;
      if (event.data.tabId === tabId) return;
      if (event.data.visible && isVisibleRef.current) {
        isLeaderRef.current = false;
      } else if (isVisibleRef.current && !event.data.visible) {
        isLeaderRef.current = true;
      }
    });

    claimLeadership();

    const handleResume = () => {
      const now = Date.now();
      const suspended = now - lastTickRef.current > suspendGapMs;
      if (suspended && isAfkExceeded()) {
        void flush(false, true, 'afk');
        lastTickRef.current = now;
        return;
      }
      if (suspended) {
        lastTickRef.current = now;
      }
    };

    const onVisibility = () => {
      const visible = document.visibilityState === 'visible';
      if (!visible) {
        syncActiveBuffer(true);
        void flush(true);
        isVisibleRef.current = false;
        return;
      }
      isVisibleRef.current = true;
      claimLeadership();
      handleResume();
      if (!isAfkExceeded()) {
        markActivity();
      } else {
        void flush(false, true, 'afk');
      }
    };

    const onActivity = () => markActivity();

    const onPageHide = () => {
      syncActiveBuffer(true);
      void flush(true);
    };

    const onFocus = () => {
      handleResume();
      if (isAfkExceeded()) {
        void flush(false, true, 'afk');
      }
    };

    const onPageShow = () => {
      handleResume();
      if (isAfkExceeded()) {
        void flush(false, true, 'afk');
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('pointerdown', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity, { passive: true });
    window.addEventListener('scroll', onActivity, { passive: true });
    window.addEventListener('touchstart', onActivity, { passive: true });

    const timer = window.setInterval(() => {
      const now = Date.now();
      const suspended = now - lastTickRef.current > suspendGapMs;

      if (suspended && isAfkExceeded()) {
        void flush(false, true, 'afk');
        lastTickRef.current = now;
        return;
      }

      if (isAfkExceeded()) {
        void flush(false, true, 'afk');
        return;
      }

      if (isActive()) {
        syncActiveBuffer();
      } else {
        lastTickRef.current = now;
      }

      if (now - lastPulseRef.current >= pulseMsRef.current) {
        void flush(false);
      }
    }, tickMsRef.current);

    publishLiveStatus();
    const uiTimer = window.setInterval(publishLiveStatus, UI_TICK_MS);

    return () => {
      window.clearInterval(timer);
      window.clearInterval(uiTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('pointerdown', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('scroll', onActivity);
      window.removeEventListener('touchstart', onActivity);
      channel?.close();
    };
  }, [
    config,
    flush,
    isActive,
    isAfkExceeded,
    markActivity,
    options.enabled,
    publishLiveStatus,
    syncActiveBuffer,
  ]);

  return {
    config,
    markActivity,
    setMediaPlaying,
    setActivityEngaged,
    liveStatus,
    isTracking: options.enabled && config != null,
  };
}

export function useLearningSessionTrackerEnabled(): boolean {
  const { hasPermission, hasRole } = usePermissions();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const reviewsWithoutProgress =
    hasRole(ROLES.DEVELOPER) ||
    hasRole(ROLES.SUPER_ADMIN) ||
    hasRole(ROLES.ADMIN) ||
    hasRole(ROLES.TEACHER);
  return (
    isAuthenticated &&
    hasRole(ROLES.STUDENT) &&
    !reviewsWithoutProgress &&
    hasPermission(PERMISSIONS.MIS_CURSOS_VER)
  );
}
